import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Truck, Package, ShoppingCart, Check, Download, ExternalLink, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';

const orderSteps = ['Order Placed', 'Processing', 'Dispatched', 'In Transit', 'Delivered', 'Acknowledged'];

const orders = [
  { 
    id: 'PO-OPS-CDS-C1-2028', 
    title: 'CDS Cycle 1 2028', 
    books: 91, 
    amount: 2847.50, 
    placedDate: '12 Sep 2026',
    delivery: 'Jan – May 2028',
    status: 'in_progress',
    progress: 40,
    currentBatch: 2,
    totalBatches: 5
  },
  { 
    id: 'PO-OPS-PILOT-C2', 
    title: 'Pilot Cycle 2', 
    books: 60, 
    amount: 1890.00, 
    placedDate: '1 Nov 2026',
    delivery: 'Nov 2026 – Feb 2027',
    status: 'in_transit',
    progress: 66,
    currentBatch: 2,
    totalBatches: 3
  },
  { 
    id: 'PO-OPS-PILOT-C1', 
    title: 'Pilot Cycle 1', 
    books: 60, 
    amount: 1920.00, 
    placedDate: '1 Aug 2026',
    delivery: 'Aug – Oct 2026',
    status: 'complete',
    progress: 100,
    currentBatch: 3,
    totalBatches: 3
  },
];

export default function SchoolOrders() {
  const handleAcknowledge = () => {
    toast.success('Delivery acknowledged successfully');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Truck className="w-7 h-7 text-[#1E4D35]" />
          My Orders & Deliveries
        </h1>
        <p className="text-slate-500 mt-1">Track all your CDS orders and deliveries</p>
      </div>

      {/* Order Cards */}
      <div className="space-y-4">
        {orders.map((order) => (
          <Card 
            key={order.id} 
            className={`overflow-hidden ${order.status === 'complete' ? 'opacity-70' : ''}`}
          >
            {order.status === 'in_progress' ? (
              <div className="bg-[#1E4D35] text-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="w-5 h-5" />
                    <div>
                      <h3 className="font-semibold">{order.title} — {order.books} books</h3>
                      <p className="text-sm text-white/80">Order No: {order.id} | Placed: {order.placedDate}</p>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-4">
                    <div>
                      <p className="font-semibold">${order.amount.toLocaleString()}</p>
                      <p className="text-xs text-white/80">Delivery: {order.delivery}</p>
                    </div>
                    <Link to={`/school/orders/${order.id}`}>
                      <Button size="sm" variant="secondary" data-testid={`view-order-${order.id}`}>
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
                      <div className="h-full bg-white" style={{ width: `${order.progress}%` }} />
                    </div>
                    <span className="text-sm">Batch {order.currentBatch} of {order.totalBatches} in transit</span>
                  </div>
                </div>
              </div>
            ) : (
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {order.status === 'in_transit' ? (
                      <Package className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Check className="w-5 h-5 text-emerald-600" />
                    )}
                    <div>
                      <h3 className="font-semibold">{order.title} — {order.books} books {order.status === 'complete' && '— COMPLETE'}</h3>
                      <p className="text-sm text-slate-500">Order No: {order.id} | {order.delivery}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {order.status === 'in_transit' && (
                      <Badge className="bg-blue-100 text-blue-700">🚚 In Transit</Badge>
                    )}
                    <Link to={`/school/orders/${order.id}`}>
                      <Button variant="outline" size="sm" data-testid={`view-order-${order.id}`}>
                        <Eye className="w-4 h-4 mr-1" />
                        {order.status === 'complete' ? 'View History' : 'View Details'}
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Delivery Tracking */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Delivery Tracking: CDS Cycle 1 2028</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="batch1">
            <TabsList>
              <TabsTrigger value="batch1">Batch 1 — Jan ✓</TabsTrigger>
              <TabsTrigger value="batch2">Batch 2 — Feb 🚚</TabsTrigger>
              <TabsTrigger value="batch3">Batch 3 — Mar ⬜</TabsTrigger>
              <TabsTrigger value="batch4">Batch 4 — Apr ⬜</TabsTrigger>
              <TabsTrigger value="batch5">Batch 5 — May ⬜</TabsTrigger>
            </TabsList>

            {/* Batch 1 - Complete */}
            <TabsContent value="batch1" className="mt-6">
              {/* Progress Stepper */}
              <div className="flex items-center justify-between mb-8">
                {orderSteps.map((step, idx) => (
                  <div key={step} className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      idx <= 5 ? 'bg-emerald-500 text-white' : 'bg-slate-200'
                    }`}>
                      <Check className="w-5 h-5" />
                    </div>
                    <span className="text-xs mt-2 text-center">{step}</span>
                    {idx === 0 && <span className="text-[10px] text-slate-500">12 Sep 2026</span>}
                    {idx === 5 && <span className="text-[10px] text-slate-500">18 Jan 2028</span>}
                  </div>
                ))}
              </div>

              <div className="p-4 bg-emerald-50 rounded-lg">
                <p className="text-emerald-800">
                  ✓ All 20 books delivered and acknowledged. No discrepancies.
                </p>
                <p className="text-sm text-emerald-600 mt-1">
                  Acknowledged by: Mrs Tan Mei Ling | 18 Jan 2028 at 10:23am
                </p>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-1" />
                    Download Delivery Order PDF
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Batch 2 - In Transit */}
            <TabsContent value="batch2" className="mt-6">
              {/* Progress Stepper */}
              <div className="flex items-center justify-between mb-8">
                {orderSteps.map((step, idx) => (
                  <div key={step} className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      idx <= 3 ? 'bg-emerald-500 text-white' : 
                      idx === 3 ? 'bg-blue-500 text-white animate-pulse' : 'bg-slate-200'
                    }`}>
                      {idx <= 3 ? <Check className="w-5 h-5" /> : <span className="text-sm">{idx + 1}</span>}
                    </div>
                    <span className="text-xs mt-2 text-center">{step}</span>
                  </div>
                ))}
              </div>

              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <p className="font-medium text-amber-800">Batch 2 has arrived! Please acknowledge receipt of 20 books by 15 Feb 2028.</p>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">
                    Tracking: SP-TRK-20280205-RGS-001 | Last update: "In transit — Singapore Postal Network | 7 Feb 2028"
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Track on SingPost
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleAcknowledge}
                      data-testid="acknowledge-delivery-btn"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Acknowledge Receipt
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Batch 3-5 - Scheduled */}
            <TabsContent value="batch3" className="mt-6">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-slate-600">Scheduled for dispatch on 5 Mar 2028</p>
              </div>
            </TabsContent>
            
            <TabsContent value="batch4" className="mt-6">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-slate-600">Scheduled for dispatch on 5 Apr 2028</p>
              </div>
            </TabsContent>
            
            <TabsContent value="batch5" className="mt-6">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-slate-600">Scheduled for dispatch on 5 May 2028</p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
